/**
 * 任務服務
 * 統一處理任務相關 API，支援 Mock/真實 API 切換
 */

import { config } from './config'
import apiClient, { ApiBusinessError } from './apiClient'
import { mockGetDriverTasks, mockUpdateTaskStatus, mockTasks } from '@/data/mockData'
import type { GrabReservation, Task, TaskStatus, TaskListResponse, ContainerGrabResponse, ShipsideCardListResponse, ShipsideCardRow, ShipsideCard } from '@/types'

/**
 * 取得司機任務列表
 * @param driverId 司機編號
 */
export async function getDriverTasks(driverId: string): Promise<TaskListResponse> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: getDriverTasks')
    return mockGetDriverTasks(driverId)
  }

  console.log('[TaskService] 呼叫 API: getDriverTasks')
  try {
    const response = await apiClient.get<TaskListResponse>('/line/drivers/me/tasks')
    return response
  } catch (error) {
    console.error('[TaskService] getDriverTasks 失敗:', error)
    return { tasks: [], summary: { assignedCount: 0, acceptedCount: 0, inProgressCount: 0, completedCount: 0 } }
  }
}

/**
 * 批次完成任務（20呎雙櫃同時完成）
 * @param missionIds 任務 ID 列表
 */
export async function batchCompleteTask(
  missionIds: string[]
): Promise<{ success: boolean; message: string }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: batchCompleteTask')
    return { success: false, message: 'Mock 模式不支援' }
  }

  console.log('[TaskService] 呼叫 API: batchCompleteTask')
  try {
    await apiClient.put('/line/missions/batch-complete', {
      missionIds,
      completedAt: new Date().toISOString(),
    })
    return { success: true, message: '任務已完成' }
  } catch (error) {
    console.error('[TaskService] batchCompleteTask 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '批次完成任務失敗' }
  }
}

/**
 * 司機取消執行中船邊櫃出口任務 (IN_PROGRESS → WAITING_GRAB)
 * <p>
 * 僅 status=20 且 io_type='E' 可取消。若帶 pairedMissionId 則同 transaction 一併取消第二筆（雙拖）。
 * @param taskId 主要 mission id
 * @param pairedTaskId 雙拖配對 mission id；null/undefined 表示只取消一筆
 */
export async function cancelMyTask(
  taskId: string,
  pairedTaskId?: string | null
): Promise<{ success: boolean; message: string; cancelledMissionIds?: string[] }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: cancelMyTask')
    const ids = pairedTaskId ? [taskId, pairedTaskId] : [taskId]
    return { success: true, message: '取消成功', cancelledMissionIds: ids }
  }

  console.log('[TaskService] 呼叫 API: cancelMyTask')
  try {
    const data = await apiClient.post<{ cancelledMissionIds: string[] }>(
      `/line/tasks/${taskId}/cancel`,
      { pairedTaskId: pairedTaskId ?? null }
    )
    return { success: true, message: '取消成功', cancelledMissionIds: data.cancelledMissionIds }
  } catch (error) {
    console.error('[TaskService] cancelMyTask 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '取消任務失敗' }
  }
}

/**
 * 拒絕任務 (ASSIGNED → WAITING_GRAB)
 *
 * @param taskId 任務 ID
 * @param reason 拒絕原因（可選）
 * @param pairedTaskId 雙拖配對 mission id（null 表示只拒絕一筆；spec §3.5.3）
 */
export async function rejectTask(
  taskId: string,
  reason?: string,
  pairedTaskId?: string | null
): Promise<{ success: boolean; message: string }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: rejectTask')
    // Mock 模式下直接返回成功
    return { success: true, message: '已拒絕任務' }
  }

  console.log('[TaskService] 呼叫 API: rejectTask')
  try {
    await apiClient.put(`/line/tasks/${taskId}/reject`, {
      reason,
      pairedTaskId: pairedTaskId ?? null,
    })
    return { success: true, message: '已拒絕任務' }
  } catch (error) {
    console.error('[TaskService] rejectTask 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '拒絕任務失敗' }
  }
}

/**
 * 貨櫃號碼更新回應
 */
export interface ContainerUpdateResult {
  taskId: string
  containerNo: string
  sealNo?: string
  inputMethod: string
  updatedAt: string
}

/**
 * 更新貨櫃號碼（掃描或手動輸入）
 * @param taskId 任務 ID
 * @param containerNo 貨櫃號碼（ISO 6346 格式：4英文+7數字）
 * @param sealNo 封條號碼（選填）
 * @param inputMethod 輸入方式：scan / manual
 * @param scanFormat 掃描格式：CODE_128 / CODE_39 / QR_CODE（選填）
 */
/**
 * 驗證貨櫃號碼（ISO 6346 Check Digit）
 * @returns 驗證通過時 resolve，失敗時 throw ApiBusinessError（code: DSP-0706 格式錯 / DSP-0707 檢核碼不符）
 */
export async function validateContainerNo(value: string): Promise<void> {
  await apiClient.get<void>('/line/container-no/validate', {
    params: { value },
  })
}

export async function updateContainerNumber(
  taskId: string,
  containerNo: string,
  sealNo?: string,
  inputMethod: 'scan' | 'manual' = 'manual',
  scanFormat?: string
): Promise<{ success: boolean; message: string; data?: ContainerUpdateResult }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: updateContainerNumber')
    // Mock 模式下直接返回成功
    return {
      success: true,
      message: '貨櫃號碼已更新',
      data: {
        taskId,
        containerNo: containerNo.toUpperCase(),
        sealNo,
        inputMethod,
        updatedAt: new Date().toISOString(),
      },
    }
  }

  console.log('[TaskService] 呼叫 API: updateContainerNumber')
  try {
    const response = await apiClient.patch<ContainerUpdateResult>(
      `/line/tasks/${taskId}/container`,
      {
        containerNo,
        sealNo,
        inputMethod,
        scanFormat,
      }
    )
    return { success: true, message: '貨櫃號碼已更新', data: response }
  } catch (error) {
    console.error('[TaskService] updateContainerNumber 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '更新貨櫃號碼失敗' }
  }
}

/**
 * 更新任務狀態（通用方法）
 * 根據目標狀態路由到對應的 API 端點
 *
 * @param taskId 任務 ID
 * @param status 新狀態
 * @param pairedTaskId 雙拖配對 mission id（accept/start 支援；null 表示只處理一筆）
 */
export async function updateTaskStatus(
  taskId: string,
  status: TaskStatus,
  pairedTaskId?: string | null
): Promise<{ success: boolean; message: string }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: updateTaskStatus')
    return mockUpdateTaskStatus(taskId, status)
  }

  console.log('[TaskService] 呼叫 API: updateTaskStatus, 目標狀態:', status)

  try {
    switch (status) {
      case 'ACCEPTED':
        await apiClient.put(`/line/tasks/${taskId}/accept`, {
          acceptedAt: new Date().toISOString(),
          pairedTaskId: pairedTaskId ?? null,
        })
        return { success: true, message: '已接受任務' }

      case 'IN_PROGRESS':
        await apiClient.put(`/line/tasks/${taskId}/start`, {
          startedAt: new Date().toISOString(),
          pairedTaskId: pairedTaskId ?? null,
        })
        return { success: true, message: '已開始執行' }

      case 'COMPLETED':
        await apiClient.put(`/line/tasks/${taskId}/complete`, {
          completedAt: new Date().toISOString(),
        })
        return { success: true, message: '任務已完成' }

      default:
        console.warn('[TaskService] 不支援的狀態轉換:', status)
        return { success: false, message: `不支援的狀態轉換: ${status}` }
    }
  } catch (error) {
    console.error('[TaskService] updateTaskStatus 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '狀態更新失敗' }
  }
}

// ==================== 船邊櫃卡片（兩層聚合） ====================

/**
 * 取得船邊櫃卡片列表
 *
 * 外層依「放櫃批次」聚合（無批次鍵者以貨櫃自成一卡），內層為規格組合明細行。
 */
// ==================== 名額卡（openspec add-import-grab-quota）====================

/** 進口卡片搶單＝登記名額（不綁 mission）。剩餘櫃數 0 才會被擋。 */
export async function reserveShipside(
  card: ShipsideCard
): Promise<{ success: boolean; message: string; data?: GrabReservation }> {
  if (config.useMock) return { success: false, message: 'Mock 模式不支援' }
  try {
    const data = await apiClient.post<GrabReservation>('/line/shipside/reservations', {
      batchKey: card.batchKey ?? null,
      containerId: card.rows[0]?.containerId ?? null,
      vslRegNo: card.vesselCode,
    })
    return { success: true, message: '已登記名額', data }
  } catch (error) {
    console.error('[TaskService] reserveShipside 失敗:', error)
    if (error instanceof ApiBusinessError) return { success: false, message: error.statusDesc }
    return { success: false, message: '登記名額失敗' }
  }
}

/** 我的名額卡（含這批還有的種類）；批次已無可綁櫃者後端讀取時標失效、不回傳 */
export async function getMyReservations(): Promise<GrabReservation[]> {
  if (config.useMock) return []
  try {
    return (await apiClient.get<GrabReservation[]>('/line/shipside/reservations/me')) ?? []
  } catch (error) {
    console.error('[TaskService] getMyReservations 失敗:', error)
    return []
  }
}

/** 到場選種類 → 綁 mission（40 / 20 各一筆；20x2 同起迄兩筆） */
export async function bindReservation(
  reservationId: string,
  kind: '40' | '20' | '20x2'
): Promise<{ success: boolean; message: string; data?: ContainerGrabResponse[] }> {
  if (config.useMock) return { success: false, message: 'Mock 模式不支援' }
  try {
    const data = await apiClient.post<ContainerGrabResponse[]>(`/line/shipside/reservations/${reservationId}/bind`, { kind })
    return { success: true, message: (data?.length ?? 0) > 1 ? '接單成功！已接 2 櫃（雙拖），任務已開始執行' : '接單成功！任務已開始執行', data }
  } catch (error) {
    console.error('[TaskService] bindReservation 失敗:', error)
    if (error instanceof ApiBusinessError) return { success: false, message: error.statusDesc }
    return { success: false, message: '接單失敗' }
  }
}

/** 司機自行取消名額 */
export async function cancelReservation(reservationId: string): Promise<{ success: boolean; message: string }> {
  if (config.useMock) return { success: false, message: 'Mock 模式不支援' }
  try {
    await apiClient.delete<unknown>(`/line/shipside/reservations/${reservationId}`)
    return { success: true, message: '已取消名額' }
  } catch (error) {
    console.error('[TaskService] cancelReservation 失敗:', error)
    if (error instanceof ApiBusinessError) return { success: false, message: error.statusDesc }
    return { success: false, message: '取消失敗' }
  }
}

export async function getShipsideCards(): Promise<ShipsideCardListResponse> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: getShipsideCards')
    return { cards: [], totalCards: 0, totalRemaining: 0 }
  }

  console.log('[TaskService] 呼叫 API: getShipsideCards')
  try {
    const response = await apiClient.get<ShipsideCardListResponse>('/line/shipside/cards')
    return response
  } catch (error) {
    console.error('[TaskService] getShipsideCards 失敗:', error)
    return { cards: [], totalCards: 0, totalRemaining: 0 }
  }
}

/**
 * 船邊櫃搶單（依卡片明細行）
 *
 * 一次只取一筆 —— 搶單會自動推進至執行中（11→13→20），而並行上限為 2 筆，
 * 批量搶第 3 筆起必然失敗，故不提供數量參數。
 *
 * @param card 所屬卡片（取 batchKey）
 * @param row  司機點選的明細行（取規格組合）
 */
export async function grabShipsideTask(
  card: ShipsideCard,
  row: ShipsideCardRow
): Promise<{ success: boolean; message: string; data?: ContainerGrabResponse }> {
  if (config.useMock) {
    console.log('[TaskService] 使用 Mock: grabShipsideTask')
    return { success: false, message: 'Mock 模式不支援' }
  }

  console.log('[TaskService] 呼叫 API: grabShipsideTask')
  try {
    const response = await apiClient.post<ContainerGrabResponse>('/line/shipside/grab', {
      batchKey: card.batchKey ?? null,
      containerId: row.containerId,
      containerSize: row.containerSize,
      emptyLoaded: row.emptyLoaded,
      fromLocation: row.fromLocation,
      toLocation: row.toLocation,
      trailerId: null,
    })
    return { success: true, message: '接單成功', data: response }
  } catch (error) {
    console.error('[TaskService] grabShipsideTask 失敗:', error)
    if (error instanceof ApiBusinessError) {
      return { success: false, message: error.statusDesc }
    }
    return { success: false, message: '搶單失敗' }
  }
}

/**
 * 取得所有任務（用於日報表等）
 * 注意：這是直接取得 mock 資料，真實 API 需要另外實作
 */
export function getAllTasks(): Task[] {
  if (config.useMock) {
    return mockTasks
  }
  // 真實 API 模式下，這個函數不應該被使用
  console.warn('[TaskService] getAllTasks 在真實 API 模式下不可用')
  return []
}
